import clsx from 'clsx'
import React, { forwardRef, useId, useMemo } from 'react'

import { SolidAlertTriangleIcon } from 'slab/icons/SolidAlertTriangle'

type Gap = 2 | 3 | 4 | 6

export interface RadioGroupProps {
  children: React.ReactNode;
  error?: React.ReactNode;
  gap?: Gap;
  layout?: 'horizontal' | 'vertical';
  disabled?: boolean;
  label?: React.ReactNode;
  name: string;
  description?: string;
}

type RadioGroupContextType = {
  name?: string;
}

export const RadioGroupContext = React.createContext<RadioGroupContextType>({
  name: undefined
})

const GAP_CLASS_MAPPING: Record<Gap, string> = {
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  6: 'gap-6',
}

function gapClass (gap: Gap) {
  return GAP_CLASS_MAPPING[gap] || 'gap-2'
}

export const RadioGroup = forwardRef<HTMLFieldSetElement, RadioGroupProps>(
  (
    {
      children,
      disabled,
      error,
      gap = 2,
      label,
      layout = 'vertical',
      name,
      description,
    },
    forwardedRef
  ) => {
    const value = React.useMemo<RadioGroupContextType>(
      () => ({
        name,
      }),
      [name]
    )

    const descriptionId = useId()
    const errorMessageId = useId()

    const describedBy = useMemo(
      () =>
        [description && descriptionId, error && errorMessageId]
          .filter(Boolean)
          .join(' '),
      [description, error]
    )

    function renderDescription () {
      if (!description) return null

      return (
        <div id={descriptionId} className='body-sm text-gray-500 mt-2'>
          {description}
        </div>
      )
    }

    function renderErrorMessage () {
      if (!error) return null

      return (
        <div className='flex gap-x-1 mt-2' id={errorMessageId}>
          <div className='text-red-400'>
            <SolidAlertTriangleIcon size={16} />
          </div>
          <div className='body-sm-bold text-red-700'>{error}</div>
        </div>
      )
    }

    return (
      <RadioGroupContext.Provider value={value}>
        <fieldset
          aria-describedby={describedBy}
          disabled={disabled}
          ref={forwardedRef}
        >
          {!!label && (
            <legend
              className={clsx('body-sm-bold mb-2', {
                'text-red-700': error,
                'text-gray-800': !error,
              })}
            >
              {label}
            </legend>
          )}
          <div
            className={clsx('flex', gapClass(gap), {
              'flex-col': layout === 'vertical',
              'flex-row flex-wrap': layout === 'horizontal',
            })}
          >
            {children}
          </div>
          {renderErrorMessage()}
          {renderDescription()}
        </fieldset>
      </RadioGroupContext.Provider>
    )
  }
)
